import type { TaskWithRelations } from '@vagusx/clients';
import { useTaskReferralDocument } from './use-task-referral';

interface ReferralSourceLineProps {
  task: TaskWithRelations;
}

/**
 * Two-line block under the task title:
 *   "Referral from {provider} at {practice} · received {N days ago}"
 *   "Auth #{n} · Status: {status}"
 * Either line is suppressed when the underlying data is missing — the
 * referral document is the only data source, but only the first line is
 * a hard requirement (we hide the whole block if no referrer info).
 */
export function ReferralSourceLine({ task }: ReferralSourceLineProps): React.ReactElement | null {
  const { document } = useTaskReferralDocument(task);
  if (!document) return null;

  const provider = document.referringProviderName?.trim();
  const practice = document.referringPracticeName?.trim();
  if (!provider && !practice) return null;

  const received = formatRelativeAge(document.createdAt);
  const authNumber = document.authNumber?.trim();
  const authStatus = document.status?.trim();

  return (
    <div className="mt-1 space-y-0.5 text-sm text-muted-foreground">
      <p>
        Referral from{' '}
        {provider ? (
          <span className="font-medium text-primary">{provider}</span>
        ) : (
          <span className="italic">unknown provider</span>
        )}
        {practice ? <> at {practice}</> : null}
        {received ? <> · received {received}</> : null}
      </p>
      {(authNumber || authStatus) && (
        <p>
          {authNumber ? <>Auth #{authNumber}</> : null}
          {authNumber && authStatus ? ' · ' : null}
          {authStatus ? (
            <>
              Status: <span className="capitalize">{authStatus}</span>
            </>
          ) : null}
        </p>
      )}
    </div>
  );
}

function formatRelativeAge(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = Date.now() - then;
  const days = Math.floor(diffMs / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}
